import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ChevronLeft,
  Download,
  TrendingUp,
  Package,
  CheckCircle,
  XCircle,
  Ban,
  DollarSign,
  BarChart3,
  AlertCircle,
  User
} from 'lucide-react';
import DateSelector from './selleranalytics/DateSelector';
import StatCard from './selleranalytics/StatCard';
import SellersList from './selleranalytics/SellersList';
import DeliveriesTable from './selleranalytics/DeliveriesTable';
import DeliverySearchButton from './selleranalytics/DeliverySearchButton';

function SellersAnalytics({ userId, setActiveComponent }) {
  const baseUrl = process.env.REACT_APP_API_URL || '';

  const [sellers, setSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const todayISO = new Date().toISOString().slice(0, 10);
  const last7ISO = new Date(Date.now() - 6 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(last7ISO);
  const [endDate, setEndDate] = useState(todayISO);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoadingSellers(true);
    setError(null);
    
    axios.get(`${baseUrl}/api/provider/sellers`, {
      params: { provider_user_id: userId }
    })
      .then(res => {
        const sellersList = res.data.users || [];
        setSellers(sellersList);
        if (sellersList.length > 0) {
          setSelectedSeller(sellersList[0]);
        }
      })
      .catch(err => {
        setError(err?.response?.data?.error || 'Error cargando vendedores.');
      })
      .finally(() => setLoadingSellers(false));
  }, [userId, baseUrl]);

  useEffect(() => {
    if (!selectedSeller || !startDate || !endDate) {
      setStats(null);
      return;
    }
    
    setLoadingStats(true);
    setError(null);
    
    axios.get(`${baseUrl}/api/provider/seller_stats`, {
      params: {
        provider_user_id: userId,
        seller_id: selectedSeller.id,
        start: startDate,
        end: endDate,
      }
    })
      .then(res => setStats(res.data))
      .catch(err => {
        setError(err?.response?.data?.error || 'Error cargando estadísticas.');
      })
      .finally(() => setLoadingStats(false));
  }, [selectedSeller, startDate, endDate, userId, baseUrl]);

  async function handleDownload() {
    if (!selectedSeller) return;
    
    try {
      const res = await axios.get(`${baseUrl}/api/provider/seller_export`, {
        params: {
          provider_user_id: userId,
          seller_id: selectedSeller.id,
          start: startDate,
          end: endDate,
        },
        responseType: 'blob',
      });
      
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_${selectedSeller.username}_${startDate}_a_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('No se pudo descargar el Excel.');
    }
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveComponent('OptionsAdmin')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-700" />
              </button>
              <div>
                <h1 className="text-xl font-extrabold text-[#B1336E] font-montserrat flex items-center gap-2">
                  <BarChart3 className="w-6 h-6" />
                  Análisis de Cuentas
                </h1>
                <p className="text-sm text-slate-600 font-montserrat">
                  Monitorea el rendimiento de tu equipo
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {/* Search Button */}
              <DeliverySearchButton userId={userId} />

              {/* Download Button */}
              <button
                onClick={handleDownload}
                disabled={!selectedSeller || loadingStats}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-montserrat"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Descargar Excel</span>
                <span className="sm:hidden">Excel</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="h-full max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="h-full grid grid-cols-1 lg:grid-cols-4 gap-6">
            
            {/* Left Sidebar - Sellers List */}
            <div className="lg:col-span-1 h-full max-h-[calc(100vh-12rem)] lg:max-h-full">
              <SellersList
                sellers={sellers}
                selectedSeller={selectedSeller}
                onSelectSeller={setSelectedSeller}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                loading={loadingSellers}
              />
            </div>

            {/* Right Content - Stats and Details */}
            <div className="lg:col-span-3 space-y-6 overflow-y-auto max-h-[calc(100vh-12rem)] lg:max-h-full">
              
              {/* Date Range Selectors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 font-montserrat mb-2">
                    Fecha Inicio
                  </label>
                  <DateSelector
                    selectedDate={startDate}
                    onDateChange={setStartDate}
                    showCalendar={showStartCalendar}
                    onToggleCalendar={() => {
                      setShowStartCalendar(!showStartCalendar);
                      setShowEndCalendar(false);
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 font-montserrat mb-2">
                    Fecha Fin
                  </label>
                  <DateSelector
                    selectedDate={endDate}
                    onDateChange={setEndDate}
                    showCalendar={showEndCalendar}
                    onToggleCalendar={() => {
                      setShowEndCalendar(!showEndCalendar);
                      setShowStartCalendar(false);
                    }}
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-red-900 font-montserrat">Error</p>
                    <p className="text-sm text-red-700 font-montserrat">{error}</p>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {loadingStats && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 flex flex-col items-center justify-center">
                  <div className="w-12 h-12 border-4 border-[#B1336E] border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-sm text-slate-600 font-montserrat">Cargando estadísticas...</p>
                </div>
              )}

              {/* Stats Display */}
              {!loadingStats && stats && (
                <>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                      icon={Package}
                      label="Total Puntos"
                      value={stats.total_delivery_points || 0}
                      gradient="from-[#B1336E] to-[#E6B43C]"
                    />
                    <StatCard
                      icon={CheckCircle}
                      label="Entregados"
                      value={stats.status_counts?.entregado || 0}
                      gradient="from-[#67A43C] to-[#E6B43C]"
                    />
                    <StatCard
                      icon={XCircle}
                      label="Rechazados"
                      value={stats.status_counts?.rechazado || 0}
                      gradient="from-red-500 to-red-600"
                    />
                    <StatCard
                      icon={Ban}
                      label="Cancelados"
                      value={stats.status_counts?.cancelado || 0}
                      gradient="from-slate-500 to-slate-600"
                    />
                  </div>

                  {/* Financial Stats */}
                  {(typeof stats.total_charge === 'number' || typeof stats.total_charged === 'number') && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {typeof stats.total_charge === 'number' && (
                        <StatCard
                          icon={DollarSign}
                          label="Monto a Cobrar"
                          value={`S/ ${stats.total_charge.toFixed(2)}`}
                          gradient="from-[#E6B43C] to-[#B1336E]"
                        />
                      )}
                      {typeof stats.total_charged === 'number' && (
                        <StatCard
                          icon={TrendingUp}
                          label="Monto Cobrado"
                          value={`S/ ${stats.total_charged.toFixed(2)}`}
                          gradient="from-[#67A43C] to-[#B1336E]"
                        />
                      )}
                    </div>
                  )}

                  {/* Deliveries Table */}
                  <DeliveriesTable items={stats.items || []} />
                </>
              )}

              {/* Empty State */}
              {!loadingStats && !stats && !error && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                  <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-base font-semibold text-slate-700 font-montserrat mb-2">
                    Selecciona un vendedor
                  </p>
                  <p className="text-sm text-slate-500 font-montserrat">
                    Elige un vendedor de la lista para ver sus estadísticas
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SellersAnalytics;